/**
 * Cache persistente per le traduzioni Gemini
 * Salva le traduzioni in localStorage per non ri-tradurle
 */

const CACHE_KEY = 'respiro_blu_translations';
const CACHE_VERSION = 1;

// Carica cache da localStorage
const loadCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      if (data.version === CACHE_VERSION) {
        return data.translations || {};
      }
    }
  } catch (e) {
    console.warn('Error loading translation cache:', e);
  }
  return {};
};

// Salva cache in localStorage
const saveCache = (translations) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      version: CACHE_VERSION,
      translations,
      updatedAt: Date.now()
    }));
  } catch (e) {
    console.warn('Error saving translation cache:', e);
  }
};

// Cache in memoria (più veloce di localStorage)
let memoryCache = loadCache();

// Coda per batch translation
let pendingTexts = [];
let batchTimeout = null;
let batchCallbacks = [];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Ottiene la chiave cache per un testo+lingua
 */
const getCacheKey = (text, lang) => `${lang}:${text}`;

/**
 * Ottiene una traduzione dalla cache
 */
export const getCachedTranslation = (text, lang) => {
  if (lang === 'it') return text;
  const key = getCacheKey(text, lang);
  return memoryCache[key] || null;
};

/**
 * Salva una traduzione in cache
 */
export const setCachedTranslation = (text, lang, translation) => {
  const key = getCacheKey(text, lang);
  memoryCache[key] = translation;
  saveCache(memoryCache);
};

/**
 * Esegue il batch di traduzioni
 */
const executeBatch = async (targetLang) => {
  const textsToTranslate = [...pendingTexts];
  const callbacks = [...batchCallbacks];
  
  pendingTexts = [];
  batchCallbacks = [];
  batchTimeout = null;
  
  if (textsToTranslate.length === 0) return;
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        texts: textsToTranslate,
        target_language: targetLang
      })
    });
    
    const data = await response.json();
    const translations = data.translations || textsToTranslate;
    
    // Salva in cache e risolvi callbacks
    textsToTranslate.forEach((text, i) => {
      const translation = translations[i] || text;
      setCachedTranslation(text, targetLang, translation);
      if (callbacks[i]) {
        callbacks[i](translation);
      }
    });
  } catch (error) {
    console.error('Batch translation error:', error);
    // In caso di errore, ritorna i testi originali
    callbacks.forEach((cb, i) => {
      if (cb) cb(textsToTranslate[i]);
    });
  }
};

/**
 * Richiede una traduzione (con batching automatico)
 */
export const requestTranslation = (text, targetLang, callback) => {
  // Se italiano, ritorna subito
  if (targetLang === 'it' || !text) {
    callback(text);
    return;
  }
  
  // Controlla cache
  const cached = getCachedTranslation(text, targetLang);
  if (cached) {
    callback(cached);
    return;
  }
  
  // Aggiungi alla coda batch
  pendingTexts.push(text);
  batchCallbacks.push(callback);
  
  // Imposta timeout per batch (50ms per raggruppare richieste vicine)
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }
  batchTimeout = setTimeout(() => executeBatch(targetLang), 50);
};

/**
 * Pulisce la cache
 */
export const clearTranslationCache = () => {
  memoryCache = {};
  localStorage.removeItem(CACHE_KEY);
};

/**
 * Ottiene statistiche cache
 */
export const getCacheStats = () => {
  const keys = Object.keys(memoryCache);
  return {
    totalEntries: keys.length,
    languages: [...new Set(keys.map(k => k.split(':')[0]))]
  };
};
